import React from "react";
import { useState } from "react";
import { View, Text, Modal, TouchableOpacity, Image } from "react-native";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { AppImages } from "../../utility/AppImages";
import { CommonButton } from "../../common/CommonButton";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { AppColors } from "../../utility/AppColors";

interface CoinsPopupProps {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  onClose: () => void;
}

const CoinsPopup: React.FC<CoinsPopupProps> = ({
  showModal = false,
  setShowModal,
  onClose,
}) => {
  const styles = useStyles();
  const deviceSize = useDeviceSize();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);

  const onCloseClick = () => {
    setShowModal(false);
    onClose();
  };

  const nextPage = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex < earnCoinsData.length - 1 ? prevIndex + 1 : prevIndex
    );
    setCurrentStep((prevStep) => prevStep + 1);
  };

  const prevPage = () => {
    setCurrentIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
    setCurrentStep((prevStep) => prevStep - 1);
  };

  const earnCoinsData = [
    {
      id: 1,
      title: "Daily Check-Ins",
      description:
        "Consistency is key. Log in every day to stack your stash and watch your rewards grow.",
      image: AppImages.icCoinsPopupImage,
      buttonText: "Daily Coins",
      buttonStyle: styles.dailyCoinsButton,
      imageStyle: styles.dailyCheckinImage,
      showIcon: true,
    },

    {
      id: 2,
      title: "Challenges",
      description:
        "• Kall someone out: Earn 20 coins for stepping up.\n" +
        "• Win like a champ: Take home 100 coins for proving your skills.\n" +
        "• Lose but show up: Get 10 coins—because effort counts.",
      image: AppImages.icChallengeIcon,
      buttonText: "Create an Event",
      buttonStyle: styles.purpleButton,
      imageStyle: styles.challengeImage,
    },

    {
      id: 3,
      title: "Event Participation",
      description:
        "• Kall Out Events: 10 coins just for showing up. \n" +
        "  • Exclusive Events: 20 coins, plus 30 bonus coins for cheering or chatting.\n",
      image: AppImages.icEventParticipation,
      buttonText: "Join an Event",
      buttonStyle: styles.purpleButton,
      imageStyle: styles.EventParticiaptionImage,
    },
    {
      id: 4,
      title: "Engagment",
      description:
        "• Cheer your favourites: Earn 10 coins per cheer (max 3 per event).\n" +
        "• Drop your take: Earn 5 coins per chat comment (max 5 per event).\n" +
        "• Vote in polls: Pick a side and grab 5 coins.\n",
      image: AppImages.icEngagment,
      buttonText: "Join an Event",
      buttonStyle: styles.purpleButton,
      imageStyle: styles.EngagmentImage,
    },
    {
      id: 5,
      title: "Milestones",
      description:
        "Hit big moments and unlock bonus coins:\n" +
        "• Play 5 challenges: 500 coins\n" +
        "• Win 10 challenges: 1,000 coins \n" +
        "• Join 5 Exclusive Events: 500 coins\n",
      image: AppImages.icMillestones,
      imageStyle: styles.MilestonesImage,
    },
    {
      id: 6,
      title: "Why Build Your Stash?",
      description:
        "Hit platform milestones and unlock rewards.\n\n" +
        "Every 10,000 coins in your stash converts to 100 tokens, the real currency for belts, tickets, and leveling up your game. Coins make every move count. The more you engage, the faster you stack your stash. Ready to turn your actions into rewards? Start building today!",
      image: null, // No image needed for this step
      imageStyle: {},
    },
  ];

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={showModal}
      onRequestClose={onCloseClick}
    >
      <View style={styles.container}>
        <View style={styles.modalView}>
          {/* Header */}
          <View style={styles.header}>
            {currentIndex !== earnCoinsData.length - 1 ? (
              <Text style={styles.headerText}>What are coins?</Text>
            ) : (
              <View /> // Empty View to align the close button correctly
            )}

            <TouchableOpacity onPress={onCloseClick}>
              <Image source={AppImages.icCloseWhite} style={styles.closeIcon} />
            </TouchableOpacity>
          </View>

          {currentIndex !== earnCoinsData.length - 1 && (
            <>
              <Text style={styles.description}>
                Coins are your reward system for making moves on the platform.
                Every action you take—whether it’s competing, watching events,
                or engaging—helps you build your stash. Collect enough, and your
                coins turn into tokens, unlocking belts, exclusive events, and
                more.
              </Text>

              <Text style={styles.howToEarnTitle}>How to Earn Coins</Text>
            </>
          )}
          {/* Dynamically Render the Earn Coins Section */}
          <View
            style={[
              styles.card,
              currentIndex === earnCoinsData.length - 1 && styles.lastStepCard, // Apply last step styles conditionally
            ]}
          >
            {earnCoinsData[currentIndex].image && (
              <Image
                source={earnCoinsData[currentIndex].image}
                style={[
                  styles.defaultImage,
                  earnCoinsData[currentIndex].imageStyle,
                ]}
              />
            )}

            <View
              style={[
                styles.cardContent,
                currentIndex === earnCoinsData.length - 1 &&
                  styles.lastStepCardContent, // Override margin only for last step
              ]}
            >
              <Text style={styles.cardTitle}>
                {earnCoinsData[currentIndex].title}
              </Text>
              <View style={styles.separator} />
              <Text style={styles.cardDescription}>
                {earnCoinsData[currentIndex].description}
              </Text>

              <TouchableOpacity style={earnCoinsData[currentIndex].buttonStyle}>
                {earnCoinsData[currentIndex].showIcon && (
                  <Image source={AppImages.icCalendar} style={styles.icon} />
                )}
                <Text style={styles.dailyCoinsText}>
                  {earnCoinsData[currentIndex].buttonText}
                </Text>
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.separator} />
          {/* Pagination & Next Button */}
          <View style={styles.footer}>
            <View style={styles.paginationContainer}>
              <Text style={styles.pagination}>
                Discover more ways to earn Coins
              </Text>
              {/* Dots (Step Indicator) */}
              <View
                style={{
                  flexDirection: "row",
                  gap: 5,
                }}
              >
                <View style={{ flexDirection: "row", gap: 5 }}>
                  {earnCoinsData.map((_, index) => (
                    <View
                      key={index}
                      style={{
                        height: 6,
                        width: 6,
                        borderRadius: 5,
                        backgroundColor:
                          currentIndex === index
                            ? AppColors.appViolentColor_6D08C3 // Active step color
                            : AppColors.white, // Inactive step color
                        borderWidth: 1,
                        borderColor:
                          currentIndex === index
                            ? AppColors.appViolentColor_6D08C3
                            : AppColors.white,
                        marginHorizontal: 2,
                      }}
                    />
                  ))}
                </View>
              </View>
            </View>
            <View style={styles.buttonContainer}>
              {currentIndex === 0 ? null : (
                <CommonButton
                  title="Back"
                  onTap={prevPage}
                  buttonStyle={[
                    styles.backButton,
                    currentIndex === 0 ? styles.disabledButton : {},
                  ]}
                  textStyle={styles.backButtonText}
                  disabled={currentIndex === 0}
                />
              )}
              <CommonButton
                title={
                  currentIndex === earnCoinsData.length - 1 ? "Finish" : "Next"
                }
                onTap={
                  currentIndex === earnCoinsData.length - 1
                    ? onCloseClick
                    : nextPage
                }
                buttonStyle={[
                  styles.nextButton,
                  currentIndex === earnCoinsData.length - 1
                    ? styles.finishButton
                    : {},
                ]}
                textStyle={styles.nextButtonText}
              />
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const useStyles = CreateResponsiveStyle(
  {
    container: {
      flex: 1,
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      justifyContent: "flex-start",
      alignItems: "center",
      paddingTop: "5%",
    },
    modalView: {
      width: "70%",
      maxWidth: 700,
      backgroundColor: AppColors.purple_453B50,
      borderRadius: 15,
      padding: 20,
      alignItems: "center",
    },
    separator: {
      width: "100%",
      height: 1,
      backgroundColor: "#C4C4C4",
      marginBottom: 16,
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      width: "100%",
      alignItems: "center",
      marginBottom: 10,
    },
    headerText: {
      ...AppFontStyle.REGULAR_ANYBODY_16,
      color: "#ffffff",
    },
    closeIcon: {
      height: 25,
      width: 25,
    },
    description: {
      ...AppFontStyle.REGULAR_ANYBODY_18,
      color: "#B8B5C3",
      textAlign: "left",
      width: "100%",
      marginBottom: 15,
      lineHeight: 24,
    },
    howToEarnTitle: {
      ...AppFontStyle.BOLD_ANYBODY_20,
      color: AppColors.white,
      alignSelf: "flex-start",
      marginBottom: 10,
    },
    card: {
      flexDirection: "row",
      backgroundColor: AppColors.purple_453B50,
      borderRadius: 12,
      padding: 15,
      width: "100%",
      alignItems: "center",
      marginBottom: 0,
    },
    coinsImage: {
      width: 240,
      height: 146,
      borderRadius: 8,
    },
    cardContent: {
      flex: 1,
      marginLeft: 30,
    },
    cardTitle: {
      ...AppFontStyle.REGULAR_ANYBODY_18,
      color: AppColors.white,
      marginBottom: 5,
    },
    cardDescription: {
      ...AppFontStyle.REGULAR_ANYBODY_16,
      color: "#B8B5C3",
    },
    dailyCoinsButton: {
      flexDirection: "row",
      backgroundColor: AppColors.purple_453B50,
      borderWidth: 0.5,
      borderColor: "#ffffff",
      borderRadius: 8,
      paddingVertical: 5,
      paddingHorizontal: 12,
      alignItems: "center",
      alignSelf: "flex-end",
      marginTop: "5%",
    },
    icon: {
      width: 18,
      height: 18,
      marginRight: 5,
    },

    purpleButton: {
      flexDirection: "row",
      backgroundColor: "#6D08C3",
      borderRadius: 10,
      paddingVertical: 5,
      paddingHorizontal: 12,
      alignItems: "center",
      alignSelf: "flex-end",
      marginTop: "3%",
    },

    dailyCoinsText: {
      ...AppFontStyle.REGULAR_ANYBODY_14,
      color: AppColors.white,
    },
    footer: {
      flexDirection: "row",
      justifyContent: "space-between",
      width: "100%",
      alignItems: "center",
    },
    pagination: {
      ...AppFontStyle.REGULAR_ANYBODY_14,
      color: AppColors.white,
      marginBottom: 7,
    },
    buttonContainer: {
      display: "flex",
      flexDirection: "row",
    },

    finishButton: {
      backgroundColor: "#6D08C3",
      borderRadius: 10,
      paddingVertical: 8,
      paddingHorizontal: 20,
      shadowOpacity: 0,
    },

    finishButtonText: {
      color: AppColors.white,
      fontSize: 16,
    },

    disabledButton: {
      opacity: 0.5,
    },
    backButton: {
      backgroundColor: "none",
      shadowOpacity: 0,
      paddingVertical: 0,
      paddingHorizontal: 20,
    },

    backButtonText: {
      color: AppColors.white,
      fontSize: 17,
      fontFamily: AppFontStyle.REGULAR_ANYBODY_14.fontFamily,
    },
    nextButton: {
      backgroundColor: "#6D08C3",
      borderRadius: 10,
      paddingVertical: 8,
      paddingHorizontal: 20,
      shadowOpacity: 0,
    },
    nextButtonText: {
      color: AppColors.white,
      fontSize: 16,
      fontFamily: AppFontStyle.REGULAR_ANYBODY_14.fontFamily,
    },
    defaultImage: {
      width: 240,
      height: 146,
    },
    dailyCheckinImage: {
      width: 220,
      height: 140,
      display: "flex",
      alignSelf: "flex-start",
    },
    challengeImage: {
      width: 75,
      height: 75,
      display: "flex",
      alignSelf: "flex-start",
    },
    EventParticiaptionImage: {
      width: 75,
      height: 75,
      display: "flex",
      alignSelf: "flex-start",
    },
    EngagmentImage: {
      width: 75,
      height: 75,
      display: "flex",
      alignSelf: "flex-start",
    },
    MilestonesImage: {
      width: 75,
      height: 75,
      display: "flex",
      alignSelf: "flex-start",
    },
    lastStepCard: {
      backgroundColor: AppColors.purple_453B50,
      borderRadius: 12,
      padding: 0,
      width: "100%",
      marginLeft: 0,
      flex: 1,
    },

    lastStepCardContent: {
      marginLeft: 0,
      marginBottom: 20,
    },
    paginationContainer: {
      display: "flex",
    },
  },
  {
    [DEVICE_SIZES.LG]: {
      container: {
        flex: 1,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        justifyContent: "flex-start",
        alignItems: "center",
        paddingTop: "8%",
      },
    },
    [DEVICE_SIZES.MD]: {
      container: {
        flex: 1,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        justifyContent: "flex-start",
        alignItems: "center",
        paddingTop: "9%",
      },
    },
    [DEVICE_SIZES.SM]: {
      container: {
        flex: 1,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        justifyContent: "flex-start",
        alignItems: "center",
        paddingTop: "9%",
      },
      modalView: {
        width: "70%",
      },
      coinsImage: {
        width: 80,
        height: 60,
      },
      dailyCoinsButton: {
        paddingVertical: 3,
        paddingHorizontal: 10,
      },
      nextButton: {
        paddingVertical: 6,
        paddingHorizontal: 16,
      },
    },
    [DEVICE_SIZES.XS]: {
      container: {
        flex: 1,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        justifyContent: "flex-start",
        alignItems: "center",
        paddingTop: "17%",
      },
      modalView: {
        width: "95%",
        padding: 15,
      },
      headerText: {
        fontSize: 18,
      },
      description: {
        fontSize: 14,
      },
      howToEarnTitle: {
        fontSize: 18,
      },
      coinsImage: {
        width: 70,
        height: 50,
      },
      dailyCoinsButton: {
        paddingVertical: 2,
        paddingHorizontal: 8,
      },
      nextButton: {
        paddingVertical: 5,
        paddingHorizontal: 14,
      },

      card: {
        flexDirection: "column",
        backgroundColor: AppColors.purple_453B50,
        borderRadius: 12,
        padding: 15,
        width: "100%",
        alignItems: "center",
        marginBottom: 0,
      },

      cardContent: {
        flex: 1,
        marginLeft: 0,
      },
      cardTitle: {
        ...AppFontStyle.REGULAR_ANYBODY_16,
        color: AppColors.white,
        marginBottom: 5,
      },

      cardDescription: {
        ...AppFontStyle.REGULAR_ANYBODY_15,
        color: "#B8B5C3",
      },

      dailyCheckinImage: {
        width: 142,
        height: 86,
        display: "flex",
        alignSelf: "center",
        marginBottom: 20,
      },

      challengeImage: {
        width: 75,
        height: 75,
        display: "flex",
        alignSelf: "center",
        marginBottom: 20,
      },
      EventParticiaptionImage: {
        width: 75,
        height: 75,
        display: "flex",
        alignSelf: "center",
        marginBottom: 20,
      },
      EngagmentImage: {
        width: 75,
        height: 75,
        display: "flex",
        alignSelf: "center",
        marginBottom: 20,
      },
      MilestonesImage: {
        width: 75,
        height: 75,
        display: "flex",
        alignSelf: "center",
        marginBottom: 20,
      },
      footer: {
        flexDirection: "column",
        justifyContent: "space-between",
        width: "100%",
        alignItems: "center",
      },
      pagination: {
        ...AppFontStyle.REGULAR_ANYBODY_14,
        color: AppColors.white,
        marginBottom: 7,
        display: "flex",
        justifyContent: "flex-start",
      },
      buttonContainer: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        width: "100%",
      },
      paginationContainer: {
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
        marginBottom: 10,
      },
    },
  }
);

export default CoinsPopup;
